import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Container, Grid, Typography, CircularProgress, Box, ToggleButton, ToggleButtonGroup, List, ListItem, ListItemAvatar, Avatar, ListItemText, ListItemSecondaryAction, Button, useMediaQuery } from '@mui/material';
import PropertyCard from './components/PropertyCard';
import GalleryModal from './components/GalleryModal';
import DetailsModal from './components/DetailsModal';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import InfoIcon from '@mui/icons-material/Info';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const themePalettes = {
  modern: {
    palette: {
      mode: 'light',
      primary: { main: '#1976d2' },
      secondary: { main: '#f50057' },
      background: { default: '#f9f9f9', paper: '#ffffff' },
      text: { primary: '#212121', secondary: '#757575' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 600, letterSpacing: '0.25px' },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '6px 16px' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: '#ffffff', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid #f0f0f0' },
        },
      },
    },
  },
  earth: {
    palette: {
      mode: 'light',
      primary: { main: '#6d4c41' },
      secondary: { main: '#a1887f' },
      background: { default: '#efebe9', paper: '#fff' },
      text: { primary: '#3e2723', secondary: '#5d4037' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 600, letterSpacing: '0.25px' },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '6px 16px' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: '#ffffff', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid #f0f0f0' },
        },
      },
    },
  },
  tech: {
    palette: {
      mode: 'light',
      primary: { main: '#00bcd4' },
      secondary: { main: '#ff9800' },
      background: { default: '#ffffff', paper: '#fff' },
      text: { primary: '#263238', secondary: '#546e7a' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 600, letterSpacing: '0.25px' },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '6px 16px' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: '#ffffff', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid #f0f0f0' },
        },
      },
    },
  },
  pastel: {
    palette: {
      mode: 'light',
      primary: { main: '#ba68c8' },
      secondary: { main: '#4dd0e1' },
      background: { default: '#fce4ec', paper: '#fff' },
      text: { primary: '#4a148c', secondary: '#7b1fa2' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 600, letterSpacing: '0.25px' },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '6px 16px' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: '#ffffff', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid #f0f0f0' },
        },
      },
    },
  },
  dark: {
    palette: {
      mode: 'dark',
      primary: { main: '#90caf9' },
      secondary: { main: '#f48fb1' },
      background: { default: '#121212', paper: '#1e1e1e' },
      text: { primary: '#ffffff', secondary: '#b0b0b0' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 600, letterSpacing: '0.25px' },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '6px 16px' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { boxShadow: '0 4px 12px rgba(0,0,0,0.4)' },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: '#1e1e1e', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.4)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid #333' },
        },
      },
    },
  },
};

function App() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [hostname, setHostname] = useState('');
  const [theme, setTheme] = useState('modern');
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [view, setView] = useState('list');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) setView(nextView);
  };

  const handleImageClick = (listing) => {
    setSelectedListing(listing);
    setGalleryOpen(true);
  };

  const handleDetailsClick = (listing) => {
    setSelectedListing(listing);
    setDetailsOpen(true);
  };

  const handleCloseGallery = () => {
    setGalleryOpen(false);
    setSelectedListing(null);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedListing(null);
  };

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    axios.get(`${API_BASE}/api/listings`)
      .then(res => setListings(res.data))
      .catch(() => setError('Failed to load listings'))
      .finally(() => setLoading(false));
    axios.get(`${API_BASE}/api/settings/currency`)
      .then(res => setCurrency(res.data.currency || 'USD'))
      .catch(() => setCurrency('USD'));
    axios.get(`${API_BASE}/api/settings/hostname`)
      .then(res => setHostname(res.data.hostname || ''))
      .catch(() => setHostname(''));
    axios.get(`${API_BASE}/api/settings/theme`)
      .then(res => setTheme(res.data.theme || 'modern'))
      .catch(() => setTheme('modern'));
    axios.get(`${API_BASE}/api/amenities`)
      .then(res => setAmenitiesList(res.data))
      .catch(() => setAmenitiesList([]));
  }, [API_BASE]);

  // Listen for theme changes from admin settings
  useEffect(() => {
    const handleThemeChange = () => {
      axios.get(`${API_BASE}/api/settings/theme`)
        .then(res => setTheme(res.data.theme || 'modern'))
        .catch(() => setTheme('modern'));
    };

    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, [API_BASE]);

  const muiTheme = useMemo(() => createTheme(themePalettes[theme] || themePalettes['modern']), [theme]);

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container maxWidth="lg" sx={{ py: 6, px: { xs: 2, sm: 3 } }}>
          <Typography variant="h3" align="center" gutterBottom sx={{ mb: 4, color: 'text.primary' }}>
            {hostname || 'Available Properties'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              aria-label="view toggle"
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: 2,
                },
              }}
            >
              <ToggleButton value="cards" aria-label="card view">
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : (
          view === 'cards' ? (
            <Grid container spacing={4}>
              {listings.map(listing => (
                <Grid item xs={12} sm={6} md={4} key={listing.id}>
                  <PropertyCard listing={listing} currency={currency} amenitiesList={amenitiesList} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <List>
              {listings.map(listing => {
                const images = listing.images && listing.images.length > 0 ? listing.images : ["https://via.placeholder.com/400x250?text=No+Image"];
                return (
                  <ListItem key={listing.id} alignItems="flex-start" sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={images[0]}
                        alt={listing.title}
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          mr: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                        onClick={() => handleImageClick(listing)}
                      >
                        {listing.title[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="h6" color="text.primary">{listing.title}</Typography>}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">{listing.location}</Typography>
                          <Typography variant="body2" color="primary.main" sx={{ mt: 0.5, fontWeight: 600 }}>{currency}{listing.price}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{listing.description}</Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<InfoIcon />}
                          onClick={() => handleDetailsClick(listing)}
                        >
                          Details
                        </Button>
                        <Button
                          href={listing.airbnb_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="small"
                          endIcon={<ViewModuleIcon />}
                        >
                          Airbnb
                        </Button>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )
        )}
        {/* Gallery Modal */}
        <GalleryModal
          open={galleryOpen}
          onClose={handleCloseGallery}
          images={selectedListing?.images && selectedListing.images.length > 0 ? selectedListing.images : ["https://via.placeholder.com/400x250?text=No+Image"]}
          title={selectedListing?.title || ''}
        />
        {/* Details Modal */}
        <DetailsModal
          open={detailsOpen}
          onClose={handleCloseDetails}
          listing={selectedListing}
          currency={currency}
          amenitiesList={amenitiesList}
        />
      </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
